import { useEffect, useRef } from "react";
import Button from "Components/Common/Button/Button";

interface Division {
  code: string;
  label: string;
  name: string;
  id: string;
  colorHex?: string;
  isIncludeBudgetBreakdown?: boolean;
}

interface QuickAddGridProps {
  divisions: Division[];
  addedCodes: Set<string>;
  onToggle: (division: Division) => void;
  disabled?: boolean;
  loading?: boolean;
  hasMore?: boolean;
  loadingMore?: boolean;
  onLoadMore?: () => void;
}

export default function QuickAddGrid({
  divisions,
  addedCodes,
  onToggle,
  disabled,
  loading,
  hasMore,
  loadingMore,
  onLoadMore,
}: QuickAddGridProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = scrollRef.current;
    const sentinel = sentinelRef.current;
    if (!root || !sentinel || !hasMore) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) onLoadMore?.();
      },
      { root, threshold: 0.1 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, onLoadMore]);
  if (loading) {
    return (
      <div className="sovQuickAddGrid">
        {Array.from({ length: 12 }).map((_, i) => (
          <div
            key={i}
            className="sovQuickAddItem"
            style={{ pointerEvents: "none" }}
          >
            <span
              className="skeleton skeletonText"
              style={{
                width: "60%",
                height: 10,
                borderRadius: 4,
                display: "block",
                marginBottom: 6,
              }}
            />
            <span
              className="skeleton skeletonText"
              style={{
                width: "90%",
                height: 10,
                borderRadius: 4,
                display: "block",
              }}
            />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="sovQuickAddGrid" ref={scrollRef}>
      {divisions.map((division) => {
        const added = addedCodes.has(division.code);
        return (
          <Button
            key={division.code}
            type="button"
            className={`sovQuickAddItem ${added ? "added" : ""} ${disabled ? "disabled" : ""}`}
            onClick={() => onToggle(division)}
            disabled={disabled}
          >
            <span className="sovQuickCode">{division.label}</span>
            <span className="sovQuickName">{division.name}</span>
            <span className="sovQuickCheck">✓ Added</span>
          </Button>
        );
      })}
      {loadingMore &&
        Array.from({ length: 4 }).map((_, i) => (
          <div
            key={`more-${i}`}
            className="sovQuickAddItem"
            style={{ pointerEvents: "none" }}
          >
            <span
              className="skeleton skeletonText"
              style={{
                width: "60%",
                height: 10,
                borderRadius: 4,
                display: "block",
                marginBottom: 6,
              }}
            />
            <span
              className="skeleton skeletonText"
              style={{
                width: "90%",
                height: 10,
                borderRadius: 4,
                display: "block",
              }}
            />
          </div>
        ))}
      {hasMore && (
        <div
          ref={sentinelRef}
          style={{ gridColumn: "1 / -1", height: 1 }}
        />
      )}
    </div>
  );
}
